/**
 * Patches fabric.Object.prototype.renderCache to use native ctx.clip() for
 * fabric.Rect clip paths instead of globalCompositeOperation 'destination-in'.
 *
 * Firefox does not correctly apply 'destination-in' compositing on offscreen
 * canvases, which is how Fabric.js 5.x implements clip paths. This patch
 * replaces that approach with native Canvas clipping, which works in all browsers.
 *
 * NOTE: This patch is tightly coupled to Fabric.js 5.x internals (renderCache,
 * _cacheCanvas, _cacheContext, isCacheDirty, _render, _drawClipPath).
 * If Fabric.js is upgraded, verify this workaround is still needed and
 * update or remove it accordingly.
 *
 * @param _fabric - The Fabric.js instance to patch
 */
export const patchClipPathRendering = (_fabric: any): void => {
  const originalRenderCache = _fabric.Object.prototype.renderCache;
  _fabric.Object.prototype.renderCache = function (options: any) {
    const clipPath = this.clipPath;
    // Only intercept non-inverted Rect clip paths; fall back for everything else
    if (!clipPath || !(clipPath instanceof _fabric.Rect) || clipPath.inverted) {
      return originalRenderCache.call(this, options);
    }

    options = options || {};
    if (!this._cacheCanvas || !this._cacheContext) {
      this._createCacheCanvas();
    }
    if (this.isCacheDirty()) {
      this.statefullCache && this.saveState({ propertySet: 'cacheProperties' });

      const ctx: CanvasRenderingContext2D = this._cacheContext;

      // Apply the clip region using native ctx.clip() (works in all browsers).
      // We must NOT restore the context after clipping, since ctx.restore() also
      // resets the clipping path. Instead we apply the clip transforms, clip,
      // then reverse them to restore the coordinate space for rendering.
      let clipTransformMatrix: number[] | null = null;
      if (clipPath.absolutePositioned) {
        clipTransformMatrix = _fabric.util.invertTransform(this.calcTransformMatrix());
      }

      ctx.save();

      // Step 1: navigate to clip path coordinate space
      if (clipTransformMatrix) {
        const m = clipTransformMatrix;
        ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
      }
      clipPath.transform(ctx);
      ctx.scale(1 / (clipPath.zoomX || 1), 1 / (clipPath.zoomY || 1));

      // Step 2: define and apply clip region (rect centered at origin in local space)
      const w = clipPath.width ?? 0;
      const h = clipPath.height ?? 0;
      ctx.beginPath();
      ctx.rect(-w / 2, -h / 2, w, h);
      ctx.clip();

      // Step 3: undo the clip transforms (but NOT the clip itself) so _render
      // draws in the cache-canvas coordinate space (centered on the object)
      ctx.scale(clipPath.zoomX || 1, clipPath.zoomY || 1);
      // Invert clipPath.transform
      const clipInverse = _fabric.util.invertTransform(clipPath.calcTransformMatrix());
      ctx.transform(clipInverse[0], clipInverse[1], clipInverse[2], clipInverse[3], clipInverse[4], clipInverse[5]);
      if (clipTransformMatrix) {
        const mInv = _fabric.util.invertTransform(clipTransformMatrix);
        ctx.transform(mInv[0], mInv[1], mInv[2], mInv[3], mInv[4], mInv[5]);
      }

      // Step 4: render the object into the clipped region
      this._renderBackground(ctx);
      this._render(ctx);
      // Deliberately skip: this._drawClipPath(ctx, this.clipPath)

      ctx.restore();
      this.dirty = false;
    }
  };
};
